import { cn } from '@forest/shared'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import { intlLocale } from '~/lib/params'
import type { ShopBalances } from '~/store/session-ledger'
import { PLACEHOLDER_BAR_CLASS } from './placeholder-bar'

const numberFormatter = new Intl.NumberFormat(intlLocale)

const pillIcons: Record<keyof ShopBalances, string> = {
  coin: coinIcon,
  gem: gemIcon,
}

// One pill's footprint — the 22px icon, taller than the 14px number, in
// 4px/8px padding: 30px, the height the boot skeleton's bars sit in — and
// the pair's spacing.
const PILL_LAYOUT_CLASS = 'flex items-center gap-0.5 px-2 py-1'
const PILL_ICON_SIZE_CLASS = 'size-[22px] shrink-0'
const PILL_HEIGHT_CLASS = 'h-[30px]'
const PAIR_CLASS = 'flex items-start gap-2'

function CurrencyPill(props: {
  currency: keyof ShopBalances
  inverted?: boolean
  pillRef?: (element: HTMLDivElement) => void
  testIdPrefix?: string
  value: number
}) {
  return (
    <div
      ref={(element) => props.pillRef?.(element)}
      data-testid={`${props.testIdPrefix ?? ''}${props.currency}-balance`}
      class={cn(PILL_LAYOUT_CLASS, 'text-sm font-semibold leading-[14px]')}
      classList={{
        'text-[#1FA880]': !props.inverted,
        'text-white [text-shadow:0_0_8.7px_rgba(0,82,79,0.42)]': props.inverted,
      }}
    >
      <img
        src={pillIcons[props.currency]}
        alt=""
        class={PILL_ICON_SIZE_CLASS}
        classList={{
          'drop-shadow-[0_0_4.35px_rgba(0,82,79,0.42)]': props.inverted,
        }}
      />
      <span>{numberFormatter.format(props.value)}</span>
    </div>
  )
}

/**
 * The coin + Time Crystal balance pill pair. One module owns the row's
 * ordering, formatting, test ids, and the flight-target contract: each
 * pill's test id sits on the same element the pill ref hands out, so reward
 * reveals measure flyer targets (ADR 0011's Claim timing) on exactly the
 * node tests address. Positioning stays with the caller via `class`.
 */
export function BalancePills(props: {
  balances: ShopBalances | undefined
  class?: string
  coinPillRef?: (element: HTMLDivElement) => void
  gemPillRef?: (element: HTMLDivElement) => void
  inverted?: boolean
  testIdPrefix?: string
}) {
  return (
    <div class={cn(PAIR_CLASS, props.class)}>
      <CurrencyPill
        currency="coin"
        inverted={props.inverted}
        pillRef={props.coinPillRef}
        testIdPrefix={props.testIdPrefix}
        value={props.balances?.coin ?? 0}
      />
      <CurrencyPill
        currency="gem"
        inverted={props.inverted}
        pillRef={props.gemPillRef}
        testIdPrefix={props.testIdPrefix}
        value={props.balances?.gem ?? 0}
      />
    </div>
  )
}

/**
 * Inert bars in the balance pills' place, for the boot skeleton (GLOSSARY.md
 * "Boot skeleton"): one bar per pill, 24px apart, at a pill's height, with no
 * balance behind them.
 */
export function BalancePillsSkeleton(props: { testId?: string }) {
  const bar = () => <div class={cn(PLACEHOLDER_BAR_CLASS, 'h-3 w-11')} />
  return (
    <div
      data-testid={props.testId}
      aria-hidden="true"
      class={cn(PILL_HEIGHT_CLASS, 'flex animate-pulse items-center gap-6')}
    >
      {bar()}
      {bar()}
    </div>
  )
}
